import React, { useMemo } from 'react';
import styled from "styled-components";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { useSelector } from "react-redux";
import { selectHolidayUserInfo, selectUsersCustomLeave, selectUsersHolidayGroupings } from '@features/holidays/store/holidays-v2.duck';
import { UsersHolidayBookingItem } from "./UsersHolidayBookingItem";
import { CustomLeaveBookingItem } from "./CustomLeaveBookingItem";
import { groupCustomLeaveDays } from "../../models/Holidays.model";
import { selectCurrentUser } from '@shared/store/auth.duck';
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CheckLineReport } from "@shared/hooks/management-structure/CheckLineReport";

export function HolidaysBookingsList(props: Props) {
  const userInfo = useSelector(selectHolidayUserInfo);
  const holidayGroupings = useSelector(selectUsersHolidayGroupings);
  const customLeave = useSelector(selectUsersCustomLeave);
  const currentUser = useSelector(selectCurrentUser);
  const {t} = useTranslation();

  // Combine holiday bookings and grouped custom-leave bookings into a single date-sorted list.
  const sortedBookings = useMemo(() => {
    const holidayItems = holidayGroupings.map((holiday) => ({ kind: 'holiday' as const, fromDate: holiday.fromDate, holiday }));
    const customItems = groupCustomLeaveDays(customLeave).map((grouping) => ({ kind: 'custom' as const, fromDate: grouping.fromDate, grouping }));
    return [...holidayItems, ...customItems].sort((a, b) => moment(a.fromDate) < moment(b.fromDate) ? -1 : 1);
  }, [holidayGroupings, customLeave]);

  const isLineReportOfCurrentUser = CheckLineReport(currentUser, userInfo?.userId);

  return (
    <Container data-test={"your-bookings-container"}>
      <HeadlineSmall style={{marginBottom: 16}}>{(!userInfo.userId || userInfo.userId === currentUser?.id) ? t('holiday.your-bookings') : t('holiday.users-bookings', {firstName: userInfo.firstName})}</HeadlineSmall>

      {sortedBookings.map((booking, key) => (
        <UsersHolidayBookingItemWrapper key={key} className={"usersHolidays__item"}>
          {booking.kind === 'holiday'
            ? <UsersHolidayBookingItem holiday={booking.holiday} currentUser={currentUser} isLineReportOfCurrentUser={isLineReportOfCurrentUser} />
            : <CustomLeaveBookingItem grouping={booking.grouping} currentUser={currentUser} userInfo={userInfo} isLineReportOfCurrentUser={isLineReportOfCurrentUser} />}
        </UsersHolidayBookingItemWrapper>
      ))}
    </Container>
  )
}

interface Props {
}

const Container = styled.div`
  width: 100%;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 24px;
  padding: 24px;
`

const UsersHolidayBookingItemWrapper = styled.div`
  width: 100%;
  border-bottom: 1px solid ${Colours.mildGrey};
  margin-bottom: 16px;
  padding-bottom: 16px;
  &:last-child {
    border: none;
    margin: 0;
    padding: 0;
  }
`
